import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, KeyRound, Loader2, Mail, PlugZap, RefreshCcw, Search, Unplug, UserCog, X, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

const LIMIT = 25;

/** Directory admin: tutti i clienti/società con ricerca, filtri per categoria e stato API.
 *  L'accesso API (chiave simk_) è concesso/ruotato/revocato solo da qui, dall'admin. */
export default function AdminDirectory() {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("all");
  const [apiFilter, setApiFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(null);
  const [newKey, setNewKey] = useState(null); // {email, api_key} — mostrata una sola volta
  const [detail, setDetail] = useState(null); // {user, data|null} — scheda azienda espansa

  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search); setPage(0); }, 350);
    return () => clearTimeout(id);
  }, [search]);

  const load = () => {
    const params = new URLSearchParams({
      search: debounced, category, api: apiFilter, skip: String(page * LIMIT), limit: String(LIMIT),
    });
    return api.get(`/admin/directory?${params}`)
      .then(({ data }) => { setItems(data.data.items || []); setTotal(data.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); });
  };
  useEffect(() => { load(); }, [debounced, category, apiFilter, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const eur = (v) => { try { return new Intl.NumberFormat(lang, { style: "currency", currency: "EUR" }).format(v || 0); } catch { return `€${(v || 0).toFixed(2)}`; } };
  const statusCls = (s) => s < 300 ? "bg-emerald-50 text-emerald-700" : s < 500 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600";
  const fmtDate = (v) => { try { return new Date(v).toLocaleString(lang, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

  const act = async (u, action) => {
    setBusy(`${action}-${u.user_id}`);
    try {
      const { data } = await api.post(`/admin/directory/${u.user_id}/api/${action}`);
      if (data.data?.api_key) setNewKey({ email: u.email, api_key: data.data.api_key });
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(null);
    }
  };

  const openDetail = async (u) => {
    setDetail({ user: u, data: null });
    try {
      const { data } = await api.get(`/admin/directory/${u.user_id}/detail`);
      setDetail({ user: u, data: data.data });
    } catch {
      setDetail(null);
      toast.error(t("misc.error"));
    }
  };

  const impersonate = async (u) => {
    try {
      const { data } = await api.post(`/admin/directory/${u.user_id}/impersonate`);
      const cur = localStorage.getItem("atlas_token");
      // salva il token admin per il ritorno (mai sovrascrivere se già in impersonazione)
      if (cur && !localStorage.getItem("atlas_impersonator")) localStorage.setItem("atlas_impersonator", cur);
      localStorage.setItem("atlas_token", data.data.token);
      window.location.href = "/account";
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    }
  };

  const sendReset = async (u) => {
    setBusy(`reset-${u.user_id}`);
    try {
      await api.post(`/admin/directory/${u.user_id}/send-password-reset`);
      toast.success(t("dir.resetSent"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(null);
    }
  };

  const catBadge = (u) => {
    const c = u.category || (u.is_dealer ? "dealer" : (u.kyc_account_type || u.kyc?.account_type || "private"));
    const cls = c === "dealer" ? "bg-[#FF6B35]/10 text-[#E8551F]" : c === "business" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-500";
    return <span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${cls}`}>{t(`dir.${c}`)}</span>;
  };

  const pill = (active) => `rounded-full px-4 py-1.5 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${active ? "bg-[#1E293B] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div data-testid="admin-directory-page" className="mt-10 space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-slate-900">{t("admin.directory")}</h2>
        <p className="text-sm text-slate-500">{t("dir.sub")}</p>
      </div>

      {newKey && (
        <div data-testid="dir-new-key" className="relative rounded-3xl bg-emerald-50 border border-emerald-200 p-6">
          <button data-testid="dir-new-key-close" onClick={() => setNewKey(null)} aria-label="chiudi"
            className="absolute top-4 right-4 rounded-full p-1.5 text-emerald-600 hover:bg-emerald-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
          <div className="font-display font-bold text-emerald-800 mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4" /> {t("dir.keyTitle")}</div>
          <p className="text-xs text-emerald-700 mb-1">{newKey.email}</p>
          <p className="text-xs text-emerald-700 mb-3">{t("dir.keyNote")}</p>
          <div className="rounded-xl bg-white border border-emerald-200 px-4 py-2.5 font-mono-t text-xs break-all select-all">{newKey.api_key}</div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 space-y-4">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input data-testid="dir-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dir.search")}
            className="w-full rounded-full border border-slate-200 pl-11 pr-4 py-3 text-sm outline-none focus:border-[#FF6B35] transition-colors" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[["all", t("dir.catAll")], ["private", t("dir.private")], ["business", t("dir.business")], ["dealer", t("dir.dealer")]].map(([k, lbl]) => (
            <button key={k} data-testid={`dir-cat-${k}`} onClick={() => { setCategory(k); setPage(0); }} className={pill(category === k)}>{lbl}</button>
          ))}
          <span className="mx-2 h-5 w-px bg-slate-200" />
          {[["all", t("dir.apiAll")], ["active", t("dir.apiActive")], ["inactive", t("dir.apiInactive")], ["requested", t("dir.apiRequested")]].map(([k, lbl]) => (
            <button key={k} data-testid={`dir-api-${k}`} onClick={() => { setApiFilter(k); setPage(0); }} className={pill(apiFilter === k)}>{lbl}</button>
          ))}
        </div>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto">
        {items === null ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : items.length === 0 ? (
          <p data-testid="dir-empty" className="py-8 text-left text-sm text-slate-400">{t("dir.empty")}</p>
        ) : (
          <table className="w-full min-w-[860px] text-sm" data-testid="dir-table">
            <thead>
              <tr className="text-left font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="pb-2 pr-3">{t("dealers.company")}</th>
                <th className="pb-2 pr-3">{t("dir.colCategory")}</th>
                <th className="pb-2 pr-3">API</th>
                <th className="pb-2 pr-3 text-right">{t("dir.colWallet")}</th>
                <th className="pb-2 pr-3 text-right">{t("dir.colOrders")}</th>
                <th className="pb-2 text-right">{t("dir.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.user_id} data-testid={`dir-row-${u.user_id}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className="py-2.5 pr-3">
                    <div className="font-medium text-slate-800">{u.dealer_company || u.kyc?.company_name || u.name || u.email}</div>
                    <div className="font-mono-t text-[10px] text-slate-400">{u.email}</div>
                    {u.match && (u.match.kind === "sim" && u.match.iccid ? (
                      <Link data-testid={`dir-match-${u.user_id}`} to={`/admin?tab=esims&q=${encodeURIComponent(u.match.iccid)}`}
                        title={t("dir.openSim")}
                        className="font-mono-t text-[9px] font-bold text-[#E8551F] mt-0.5 inline-block hover:underline">
                        ICCID {u.match.value} ↗
                      </Link>
                    ) : (
                      <div data-testid={`dir-match-${u.user_id}`} className="font-mono-t text-[9px] font-bold text-[#E8551F] mt-0.5">
                        {t("dir.matchOrder")} {u.match.value}
                      </div>
                    ))}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      {catBadge(u)}
                      {u.is_dealer && u.kyc_done === false && (
                        <span data-testid={`dir-kyc-missing-${u.user_id}`} className="rounded-full bg-amber-50 text-amber-700 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">{t("dir.kycMissing")}</span>
                      )}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3">
                    {u.api_enabled ? (
                      <span>
                        <span data-testid={`dir-api-badge-${u.user_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t("dir.apiOn")}
                        </span>
                        <div className="font-mono-t text-[10px] text-slate-400 mt-1">{u.api_key_prefix || ""}</div>
                      </span>
                    ) : (u.api_request || {}).status === "pending" ? (
                      <span data-testid={`dir-pending-${u.user_id}`} className="rounded-full bg-amber-50 text-amber-700 px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">{t("dir.pending")}</span>
                    ) : (
                      <span className="font-mono-t text-[10px] text-slate-300">—</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{eur(u.wallet_balance)}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{u.orders_count ?? 0}</td>
                  <td className="py-2.5 text-right">
                    <span className="inline-flex items-center gap-1.5">
                      {!u.is_admin && (
                        <button data-testid={`dir-impersonate-${u.user_id}`} onClick={() => impersonate(u)} title={t("dir.impersonate")}
                          className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-amber-400 hover:text-amber-600 transition-colors">
                          <UserCog className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button data-testid={`dir-reset-${u.user_id}`} onClick={() => sendReset(u)} disabled={busy} title={t("dir.resetPw")}
                        className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-60">
                        {busy === `reset-${u.user_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                      </button>
                      <button data-testid={`dir-detail-${u.user_id}`} onClick={() => openDetail(u)} title={t("dir.info")}
                        className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      {!u.api_enabled && (u.api_request || {}).status === "pending" && (
                        <button data-testid={`dir-reject-${u.user_id}`} onClick={() => act(u, "reject")} disabled={busy} title={t("dir.reject")}
                          className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-red-400 hover:text-red-500 transition-colors">
                          {busy === `reject-${u.user_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {u.api_enabled ? (
                        <>
                          <button data-testid={`dir-rotate-${u.user_id}`} onClick={() => act(u, "rotate")} disabled={busy} title={t("dealers.rotate")}
                            className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                            {busy === `rotate-${u.user_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
                          </button>
                          <button data-testid={`dir-disable-${u.user_id}`} onClick={() => act(u, "disable")} disabled={busy} title={t("dir.disable")}
                            className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-red-400 hover:text-red-500 transition-colors">
                            {busy === `disable-${u.user_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />}
                          </button>
                        </>
                      ) : (u.category !== "private") && (
                        <button data-testid={`dir-enable-${u.user_id}`} onClick={() => act(u, "enable")} disabled={busy} title={t("dir.enable")}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#1E293B] text-white px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-60">
                          {busy === `enable-${u.user_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlugZap className="w-3 h-3" />} {t("dir.enable")}
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {total > LIMIT && (
          <div className="flex items-center justify-between pt-4 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400">
            <button data-testid="dir-prev" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="rounded-full border border-slate-200 px-4 py-1.5 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">←</button>
            <span data-testid="dir-page-info">{page + 1} / {pages} · {total}</span>
            <button data-testid="dir-next" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}
              className="rounded-full border border-slate-200 px-4 py-1.5 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">→</button>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent data-testid="dir-detail-dialog" className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{detail?.user?.dealer_company || detail?.user?.kyc?.company_name || detail?.user?.name || detail?.user?.email}</DialogTitle>
            <DialogDescription className="sr-only">{t("dir.info")}</DialogDescription>
          </DialogHeader>
          {detail && (detail.data === null ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
          ) : (
            <div className="space-y-6">
              <div data-testid="dir-detail-info" className="flex flex-wrap items-center gap-2 font-mono-t text-[10px]">
                <span className="rounded-full bg-slate-100 text-slate-600 px-3 py-1">{detail.data.user.email}</span>
                {catBadge(detail.data.user)}
                {detail.data.user.api_enabled && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 font-bold uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t("dir.apiOn")} · {detail.data.user.api_key_prefix}
                  </span>
                )}
                <span className="rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 uppercase tracking-widest">{t("dir.colWallet")} {eur(detail.data.user.wallet_balance)}</span>
                <span className="text-slate-400">{(detail.data.user.created_at || "").slice(0, 10)}</span>
              </div>

              <div data-testid="dir-detail-orders">
                <h4 className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{t("dir.recentOrders")}</h4>
                {detail.data.orders.length === 0 ? <p className="text-xs text-slate-300">{t("dir.noData")}</p> : (
                  <div className="divide-y divide-slate-50">
                    {detail.data.orders.map((o) => (
                      <div key={o.order_id} data-testid={`dir-detail-order-${o.order_id}`} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{o.package_title}</div>
                          <div className="font-mono-t text-[10px] text-slate-400">{o.code || o.order_id} · {fmtDate(o.created_at)}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono-t text-xs font-bold">{eur(o.price)}</div>
                          <div className="font-mono-t text-[9px] uppercase text-slate-400">{o.status}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div data-testid="dir-detail-sims">
                <h4 className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{t("dir.sims")}</h4>
                {detail.data.sims.length === 0 ? <p className="text-xs text-slate-300">{t("dir.noData")}</p> : (
                  <div className="divide-y divide-slate-50">
                    {detail.data.sims.map((s) => (
                      <div key={s.iccid} data-testid={`dir-detail-sim-${s.iccid}`} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{s.package_title}</div>
                          <div className="font-mono-t text-[10px] text-slate-400">…{String(s.iccid).slice(-8)} · {s.destination || ""}</div>
                        </div>
                        <span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest shrink-0 ${s.suspended ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>
                          {s.suspended ? "sospesa" : (s.status || "—")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div data-testid="dir-detail-calls">
                <h4 className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{t("dir.apiCalls")}</h4>
                {detail.data.api_calls.length === 0 ? <p className="text-xs text-slate-300">{t("dir.noData")}</p> : (
                  <div className="divide-y divide-slate-50">
                    {detail.data.api_calls.map((l) => (
                      <div key={l.log_id} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0 font-mono-t text-xs text-slate-600 truncate">{l.method} {l.path}</div>
                        <div className="shrink-0 flex items-center gap-2">
                          <span className={`rounded-full px-2 py-0.5 font-mono-t text-[10px] font-bold ${statusCls(l.status)}`}>{l.status}</span>
                          <span className="font-mono-t text-[10px] text-slate-400">{fmtDate(l.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div data-testid="dir-detail-wallet">
                <h4 className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">{t("dir.walletMoves")}</h4>
                {detail.data.wallet.length === 0 ? <p className="text-xs text-slate-300">{t("dir.noData")}</p> : (
                  <div className="divide-y divide-slate-50">
                    {detail.data.wallet.map((w) => (
                      <div key={w.tx_id} className="flex items-center justify-between gap-3 py-2">
                        <div className="min-w-0">
                          <div className="text-xs text-slate-600 truncate">{w.note || w.type}</div>
                          <div className="font-mono-t text-[10px] text-slate-400">{fmtDate(w.created_at)}</div>
                        </div>
                        <div className={`font-mono-t text-xs font-bold shrink-0 ${w.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                          {w.amount >= 0 ? "+" : ""}{eur(w.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
